import { nextTick } from "vue";
import { defineStore } from "pinia";
import { relativeTime } from "../util/dates.mjs";
import { asyncFetchJSON, postJSON } from "../util/fetchLib.mjs";
import { logger } from "../util/logger.mjs";
import { objectAsURL } from "../util/urlSearchParams.mjs";
import { asStateAbbreviation } from "../util/stateMapping.mjs";
import useProductDataStore from "./productDataStore.mjs";
import { EProductReviewsErrorState, ESortCategory, EIndividualReviewFeedbackOptions } from "../types/pinia/ProductReviewsModel.mjs";
import useProductAppPropsStore from "./productAppPropsStore.mjs";
function productReviewPhotoTransformer(photos, reviewIndex) {
  return photos == null ? void 0 : photos.map((photo, index) => ({
    caption: photo.Caption,
    id: photo.Id,
    sizes: photo.Sizes,
    sizesOrder: photo.SizesOrder,
    photoIndex: index,
    reviewIndex: reviewIndex ?? 0
  }));
}
function individualReviewTransformer(review, reviewIndex) {
  var _a;
  return {
    ...review,
    badges: review.badges.some((badge) => badge === "incentivizedReview") ? "Received free product" : void 0,
    timeSinceSubmission: relativeTime(review.submissionTime),
    contextDataValues: Object.values(review.contextDataValues),
    photos: productReviewPhotoTransformer(review.photos, reviewIndex ?? 0),
    clientResponses: (_a = review.clientResponses) == null ? void 0 : _a.map((response) => ({
      ...response,
      publishTime: relativeTime(response.Date),
      reportedStatus: { reported: false, helpful: EIndividualReviewFeedbackOptions.NONE }
    }))
  };
}
function specificationReviewTransformer(review) {
  var _a, _b;
  if (!review) return { id: 0 };
  return {
    ...review,
    reviewId: review.id,
    author: {
      id: review.authorId,
      name: review.userNickname,
      location: review.userLocation
    },
    badges: ((_a = review.badgesOrder) == null ? void 0 : _a.some((badge) => badge === "incentivizedReview")) ? "Received free product" : void 0,
    contextDataValues: review.contextDataValues ? Object.values(review.contextDataValues) : [],
    timeSinceSubmission: relativeTime(review.submissionTime),
    text: review.reviewText,
    rating: review.rating,
    photos: productReviewPhotoTransformer(review.photos),
    clientResponses: (_b = review.clientResponses) == null ? void 0 : _b.map((response) => ({
      ...response,
      publishTime: relativeTime(response.Date)
    })),
    wouldRecommend: review.isRecommended,
    userLocation: review.userLocation ? asStateAbbreviation(review.userLocation) : "",
    syndicated: review.isSyndicated,
    styleId: review.productId,
    dateModified: review.lastModificationTime
  };
}
const useProductReviewsStore = defineStore("productReviewsStore", {
  state: () => ({
    errorState: EProductReviewsErrorState.NONE,
    filteredReviews: [],
    filterStarRating: {},
    reviews: useProductDataStore().product.reviewPage,
    reportedReviews: {},
    showSubmissionForm: false,
    summary: useProductDataStore().product.summaryV2,
    sortCategory: ESortCategory.RELEVANCY,
    userStars: 0
  }),
  getters: {
    getHelpfulReviews(state) {
      var _a, _b;
      const {
        mostCriticalReview: criticalData,
        mostFavorableReview: favorableData
      } = state.summary;
      const mostCriticalReview = specificationReviewTransformer(
        criticalData
      );
      const mostFavorableReview = specificationReviewTransformer(
        favorableData
      );
      return {
        mostCriticalReview: {
          ...mostCriticalReview,
          reportedStatus: {
            ...(_a = state.reportedReviews) == null ? void 0 : _a[mostCriticalReview == null ? void 0 : mostCriticalReview.id]
          }
        },
        mostFavorableReview: {
          ...mostFavorableReview,
          reportedStatus: {
            ...(_b = state.reportedReviews) == null ? void 0 : _b[mostFavorableReview == null ? void 0 : mostFavorableReview.id]
          }
        }
      };
    },
    getParsedReviews(state) {
      var _a;
      const { reviews } = state;
      return {
        ...reviews,
        content: (_a = reviews == null ? void 0 : reviews.content) == null ? void 0 : _a.map((entry) => {
          var _a2;
          return {
            ...individualReviewTransformer(entry),
            reportedStatus: {
              ...(_a2 = state.reportedReviews) == null ? void 0 : _a2[entry.id]
            }
          };
        })
      };
    },
    getReviewPhotos(state) {
      var _a;
      if (state.summary === null) return [];
      const photos = [];
      (_a = state.summary.reviewsWithPhotos) == null ? void 0 : _a.forEach((review, index) => {
        const photoArr = productReviewPhotoTransformer(review.photos, index);
        photos.push(...photoArr);
      });
      return photos;
    },
    getReviewsWithPhotos(state) {
      var _a;
      const { summary } = state;
      if (!(summary == null ? void 0 : summary.reviewsWithPhotos)) return [];
      return (_a = summary.reviewsWithPhotos) == null ? void 0 : _a.map((entry, index) => {
        var _a2;
        return {
          ...individualReviewTransformer(entry, index),
          reportedStatus: {
            ...(_a2 = state.reportedReviews) == null ? void 0 : _a2[entry.id]
          }
        };
      });
    },
    productReviewsProps(state) {
      var _a, _b;
      return {
        campaign: (_a = useProductAppPropsStore().appPropsMap.textStrings) == null ? void 0 : _a.BVCampaignId,
        reviews: this.getParsedReviews,
        summary: state.summary ?? {},
        userStars: state.userStars,
        showReviews: ((_b = state.summary) == null ? void 0 : _b.totalReviewCount) > 0,
        showSubmissionForm: state.showSubmissionForm,
        filterStarRating: state.filterStarRating,
        reviewPhotos: this.getReviewPhotos,
        reviewsWithPhotos: this.getReviewsWithPhotos,
        helpfulReviews: this.getHelpfulReviews,
        errorState: state.errorState
      };
    },
    filterStarsString(state) {
      return Object.keys(state.filterStarRating).filter((k) => this.filterStarRating[k]).join(",");
    }
  },
  actions: {
    async handleSort({
      sort,
      eq = "",
      errorMessage,
      dir
    }) {
      var _a, _b;
      const endpoint = (_b = (_a = useProductAppPropsStore().appPropsMap) == null ? void 0 : _a.endpoints) == null ? void 0 : _b.reviewsPaginationAndSort;
      const { offsetSize } = this.reviews;
      const { productId } = this.summary;
      const { filterStarsString, sortCategory } = this;
      const size = 10;
      const qp = objectAsURL({
        size,
        // dir is not specified for filtering, it has to be zero to reset to beggining
        offset: dir !== void 0 ? offsetSize + dir * size : 0,
        sort: sort || sortCategory,
        eq: eq || filterStarsString
      });
      const url = `${endpoint}${eq.length > 0 ? "filtered/" : ""}${productId}?${qp}`;
      const response = await asyncFetchJSON(url);
      if (response.status === 200) {
        this.errorState = EProductReviewsErrorState.NONE;
        logger.log("handleSort Success", {
          status: response.status,
          ok: response.ok
        });
      } else {
        this.errorState = EProductReviewsErrorState.REVIEW_LOADING_ERROR;
        logger.error("handleSort error:", {
          status: response.status,
          ok: response.ok,
          message: errorMessage
        });
      }
      return response;
    },
    async handleUserStarClick(rating) {
      this.showSubmissionForm = false;
      this.userStars = rating;
      await nextTick();
      this.showSubmissionForm = true;
    },
    async handleIndividualReviewFeedback(feedbackBody) {
      var _a;
      const { appPropsMap } = useProductAppPropsStore();
      const endpoint = (_a = appPropsMap == null ? void 0 : appPropsMap.endpoints) == null ? void 0 : _a.individualReviewInteractions;
      const response = await postJSON(endpoint, feedbackBody);
      const existingEntry = this.reportedReviews[feedbackBody.contentId] || { helpful: EIndividualReviewFeedbackOptions.NONE, reported: false };
      let newEntry = {};
      if (response.ok) {
        logger.log("succesful response:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });
        const isReported = feedbackBody.feedback === EIndividualReviewFeedbackOptions.REPORT;
        const isHelpful = feedbackBody.feedback === EIndividualReviewFeedbackOptions.HELPFUL;
        const isUnhelpful = feedbackBody.feedback === EIndividualReviewFeedbackOptions.UNHELPFUL;
        const unhelpfulStatus = isUnhelpful ? EIndividualReviewFeedbackOptions.UNHELPFUL : EIndividualReviewFeedbackOptions.NONE;
        const helpfulStatus = isHelpful ? EIndividualReviewFeedbackOptions.HELPFUL : unhelpfulStatus;
        const newHelpfulStatus = existingEntry.helpful !== EIndividualReviewFeedbackOptions.NONE ? existingEntry.helpful : helpfulStatus;
        newEntry = {
          helpful: newHelpfulStatus,
          reported: isReported || existingEntry.reported,
          status: response.status
        };
      } else {
        logger.error("Error response:", {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        });
        newEntry = {
          helpful: existingEntry.helpful,
          reported: existingEntry.reported,
          status: response.status
        };
      }
      this.reportedReviews = {
        ...this.reportedReviews,
        [feedbackBody.contentId]: newEntry
      };
    },
    async handleFilterByStarRating(rating) {
      const eq = Object.keys(rating).filter((k) => rating[k]).join(",");
      const response = await this.handleSort({
        eq,
        errorMessage: "stars error"
      });
      if (response.ok) {
        if (eq === "") {
          this.reviews = response.json;
          this.filteredReviews = [];
        } else {
          const { filteredReviews, totalFilteredReviews } = response.json;
          this.reviews.minimumElementOnPage = 0;
          this.filteredReviews = filteredReviews;
          this.reviews.content = filteredReviews.slice(0, 10);
          this.reviews.totalPages = Math.ceil(totalFilteredReviews / 10);
          this.reviews.totalElements = totalFilteredReviews;
        }
        this.filterStarRating = rating;
      }
    },
    async handleReviewsSort(payload) {
      const eq = this.filterStarsString || "";
      const response = await this.handleSort({
        sort: payload.sortCategory,
        errorMessage: "sort error",
        dir: 0,
        eq
      });
      if (response.ok) {
        if (eq === "") {
          this.reviews = response.json;
        } else {
          const { filteredReviews } = response.json;
          const { minimumElementOnPage } = this.reviews;
          this.filteredReviews = filteredReviews;
          this.reviews.content = this.filteredReviews.slice(minimumElementOnPage / 10, minimumElementOnPage / 10 + 10);
        }
        this.sortCategory = payload.sortCategory;
      }
    },
    async handlePagination(direction) {
      if (this.filteredReviews.length > 0) {
        const newOffset = this.reviews.offsetSize + direction * 10;
        const newMinElementOnPage = this.reviews.minimumElementOnPage + direction * 100;
        this.reviews.offsetSize = newOffset;
        this.reviews.minimumElementOnPage = newMinElementOnPage;
        this.reviews.content = this.filteredReviews.slice(newMinElementOnPage / 10, newMinElementOnPage / 10 + 10);
      } else {
        const response = await this.handleSort({
          sort: this.sortCategory,
          errorMessage: "pagination error",
          dir: direction,
          eq: ""
        });
        if (response.ok) {
          this.reviews = response.json;
        }
      }
    }
  }
});
export {
  useProductReviewsStore as default
};
//# sourceMappingURL=productReviewsStore.mjs.map
